// `React` must be in SCOPE, not merely resolvable: the kiosk renderchecks run ts-node with
// `"jsx": "react"` (the classic runtime), which compiles this file's JSX to
// `React.createElement`. The app bundlers use the automatic runtime and need no import, so a
// missing one here builds clean and throws only in those node-only gates.
import React from "react";
import Svg, { Path, Rect } from "react-native-svg";

/**
 * The clock on the kiosk's idle warning ("Still there?").
 *
 * Figma export — the `d` below is that export's verbatim, on its native 40×40 grid.
 *
 * ⚠️ **It draws its OWN tinted disc**, unlike every other icon in this folder: the `<Rect>` is
 * the pale amber circle the design puts behind the clock, not a bounding box. A caller must
 * therefore NOT wrap it in a badge `View` of its own — that would stack two discs.
 *
 * The two colours are exported because the countdown pill beside it is drawn in the same amber,
 * and the kiosk theme has no rung for it: `theme.attention`/`onAttention` is a browner sand
 * chosen for the customiser's "Required" badge. Rather than restate the hexes at the call site,
 * the asset is the one definition of its own palette.
 */

/** The clock's stroke — and the countdown's ink, which the design draws in the same amber. */
export const TIMER_ACCENT = "#FABF26";

/**
 * The disc behind it. The export writes this as `#FF7A00` at 8.23% opacity; passing the two
 * parts separately keeps the rendered colour identical to the design over any backdrop,
 * whereas pre-blending it would only match over white.
 */
export const TIMER_DISC = "#FF7A00";
export const TIMER_DISC_OPACITY = 0.0823529;

export interface TimerIconProps {
  /** Rendered box. The 40×40 artwork scales with it; the disc is always a full circle. */
  size?: number;
  /** The clock stroke. Defaults to the design's amber. */
  color?: string;
  /** The disc behind the clock. Pass `"transparent"` to draw the clock alone. */
  discColor?: string;
  discOpacity?: number;
}

export function TimerIcon({
  size = 40,
  color = TIMER_ACCENT,
  discColor = TIMER_DISC,
  discOpacity = TIMER_DISC_OPACITY,
}: TimerIconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <Rect width={40} height={40} rx={20} fill={discColor} fillOpacity={discOpacity} />
      <Path
        d="M20 14.9996V20L23.3336 21.6668M28.334 20C28.334 24.6028 24.6028 28.334 20 28.334C15.3973 28.334 11.666 24.6028 11.666 20C11.666 15.3973 15.3973 11.666 20 11.666C24.6028 11.666 28.334 15.3973 28.334 20Z"
        stroke={color}
        strokeLinecap="round"
      />
    </Svg>
  );
}
TimerIcon.displayName = "TimerIcon";
export default TimerIcon;
